// ------------------------------------
// Constants
// ------------------------------------
import axios from 'axios'
import constants from '../lib/constants'
import _ from 'lodash'
import * as math from 'mathjs'

export const CREATE_QC = 'CREATE_QC'
export const UPDATE_QC = 'UPDATE_QC'
export const LIST_QC = 'LIST_QC'
// ------------------------------------
// Actions
// ------------------------------------

const intervalIds = {}

const LEVELS = ['low', 'normal', 'high']

export const calculateBiasForLevel = (qcResults, level, controlMaterial) => {
  const target = controlMaterial[level].wbc_mean
  const jobs = qcResults[`${level}_jobs`]
  const averageWBC = getAverageWBC(jobs)
  return (averageWBC - target) / target
}

export const getAverageWBC = (jobs) => {
  const finishedJobs = _.filter(jobs, (job) => job.wbc_count)
  return _.sumBy(finishedJobs, (job) => job.wbc_count) / _.size(finishedJobs)
}

export const getAverageBias = (qcResults, controlMaterial) => {
  const biases = _.map(LEVELS, (level) => {
    return Math.abs(calculateBiasForLevel(qcResults, level, controlMaterial))
  })
  return _.mean(biases)
}

export const getCV = (qcResults, level) => {
  const finishedJobs = _.filter(qcResults[`${level}_jobs`], (job) => job.wbc_count)
  if (_.size(finishedJobs) > 0) {
    const stdDev = math.std(_.map(finishedJobs, (j) => j.wbc_count))
    const mean = math.mean(_.map(finishedJobs, (j) => j.wbc_count))
    return stdDev / mean
  } else {
    return 0
  }
}

export const countOutOfRange = (jobs, levelMaterial) => {
  return _.filter(jobs, (job) =>
    job && typeof job.wbc_count === 'number' &&
    (job.wbc_count < levelMaterial.wbc_min || job.wbc_count > levelMaterial.wbc_max)
  ).length;
}

export const areAllInitialJobsComplete = (qcResults) => {
  const levels = ['low', 'normal', 'high'];
  return levels.every(level => {
    const jobs = qcResults[`${level}_jobs`] || [];
    const initialJobs = jobs.slice(0, 3);
    return initialJobs.length === 3 && initialJobs.every(job => 
      job && job.wbc_count && typeof job.wbc_count === 'number'
    );
  });
}

export const countTotalOutOfRange = (qcResults, controlMaterial) => {
  const levels = ['low', 'normal', 'high'];
  return levels.reduce((total, level) => {
    const jobs = qcResults[`${level}_jobs`] || [];
    const initialJobs = jobs.slice(0, 3);
    const levelMaterial = controlMaterial[level];
    return total + countOutOfRange(initialJobs, levelMaterial);
  }, 0);
}

export const createQc = ({ operator, deviceString, controlMaterialId, testStripLot, note }) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/qc_test_strips/create`, {
      operator,
      device_string: deviceString,
      control_material_id: controlMaterialId,
      test_strip_lot: testStripLot,
      note
    })
    .then((response) => {
      dispatch({
        type: UPDATE_QC,
        payload: response.data
      })
      return response.data
    })
    .catch((err) => {
      console.error(err)
    })
  }
}

export const updateQc = (id, { operator, deviceString, controlMaterialId, testStripLot, note, flowTimes }) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/qc_test_strips/${id}/update`, {
      operator,
      device_string: deviceString,
      control_material_id: controlMaterialId,
      test_strip_lot: testStripLot,
      note,
      flow_times: flowTimes
    })
    .then((response) => {
      return dispatch({
        type: UPDATE_QC,
        payload: response.data
      })
    })
  }
}

export const isQcComplete = (qc) => {
  const hasQcJobs = !_.isEmpty(qc.low_jobs) &&
  !_.isEmpty(qc.normal_jobs) &&
  !_.isEmpty(qc.high_jobs)

  const jobsFinished = _.every(_.map(LEVELS, (level) => {
    return _.every(
      _.map(
        qc[`${level}_jobs`], (job) => job.job_status && constants.FINISHED_JOB_STATUSES.indexOf(job.job_status) > -1
      )
    )
  })) &&
  _.size(qc.low_jobs) === 3 &&
  _.size(qc.normal_jobs) === 3 &&
  _.size(qc.high_jobs) === 3

  return hasQcJobs && jobsFinished
}

export const getQc = (qcId) => {
  return (dispatch, getState) => {
    return axios.get(`${constants.API_URL}/qc_test_strips/${qcId}/`)
    .then((response) => {
      dispatch({
        type: UPDATE_QC,
        payload: response.data
      })
      return response.data
    })
  }
}

export const listQc = () => {
  return (dispatch, getState) => {
    return axios.get(`${constants.API_URL}/qc_test_strips/list`)
    .then((response) => {
      dispatch({
        type: LIST_QC,
        payload: response.data.qcs
      })
      return response.data.qcs
    })
  }
}

export const pollQcResults = (dispatch, qcId) => {
  const pollingFunction = () => {
    dispatch(getQc(qcId))
    .then((qcResults) => {
      if (isQcComplete(qcResults)) {
        clearInterval(intervalIds[qcId])
        delete intervalIds[qcId]
      }
    })
  }
  pollingFunction()
  if (!intervalIds[qcId]) {
    intervalIds[qcId] = setInterval(pollingFunction, 30000)
  }
}

export const deleteQcJob = (qcId, job) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/qc_test_strips/${qcId}/job/${job.job_id}/delete`)
    .then((response) => {
      dispatch({
        type: UPDATE_QC,
        payload: response.data
      })
      return response.data
    })
  }
}

export const cancelQcJob = (qcId, job) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/qc_test_strips/${qcId}/job/${job.job_id}/cancel`)
    .then((response) => {
      dispatch({
        type: UPDATE_QC,
        payload: response.data
      })
      return response.data
    })
  }
}

export const runIndividualQc = (qcId, level) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/qc_test_strips/${qcId}/run`, {
      range: level
    })
    .then((resp) => resp.jobId)
    .then((jobId) => {
      const state = getState()
      const jobs = state.qc[qcId][`${level}_jobs`] || []
      const newJobs = jobs.concat([{ job_id: jobId }])
      dispatch({
        type: UPDATE_QC,
        payload: {
          id: qcId,
          [`${level}_jobs`]: newJobs
        }
      })
      pollQcResults(dispatch, qcId)
      return jobId
    })
  }
}

export const certifyQc = (qcId) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/qc_test_strips/${qcId}/certify`)
    .then((resp) => {
      dispatch({
        type: UPDATE_QC,
        payload: resp.data
      })
      return resp.data
    })
  }
}

const job_level_regex = /\S+_jobs/
const ACTION_HANDLERS = {
  [UPDATE_QC]  : (state, action) => {
    const qc = action.payload
    return _.mergeWith({}, state, {
      [qc.id]: qc
    }, (objValue, srcValue, key, object, source, stack) => {
      // custom merge function to make sure removed jobs are reflected in updates
      if (job_level_regex.test(key)) {
	 return srcValue
      }
    })
  },
  [LIST_QC]    : (state, action) => {
    const qcs = action.payload
    const qcObject = _.keyBy(qcs, 'id')
    return {
      ...state,
      ...qcObject
    }
  }
}

const initialState = {}

export default function qcReducer (state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]

  return handler ? handler(state, action) : state
}



// WEBPACK FOOTER //
// ./src/store/qc.js